export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;
export const RELOAD_AUTO_APPLY_WINDOW_MS = 5000;
export const APPLIED_UPDATE_STORAGE_KEY = 'wfm-applied-update';
export const PRELOAD_GUARD_STORAGE_KEY = 'wfm-preload-reload';

const SEMVER_LIKE_RE = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/;

function getStorage(storage) {
  if (storage) return storage;
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
}

export function parseVersionPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const raw = typeof payload.version === 'string' ? payload.version.trim() : '';
  if (!raw || raw === 'dev' || !SEMVER_LIKE_RE.test(raw)) return null;
  return raw;
}

export function hasVersionChanged(currentVersion, remoteVersion) {
  const current = typeof currentVersion === 'string' ? currentVersion.trim() : '';
  const remote = typeof remoteVersion === 'string' ? remoteVersion.trim() : '';
  if (!current || !remote || current === 'dev' || remote === 'dev') return false;
  if (!SEMVER_LIKE_RE.test(current) || !SEMVER_LIKE_RE.test(remote)) return false;
  return current !== remote;
}

export function isExplicitReloadNavigation(navType) {
  return navType === 'reload';
}

export function wasUpdateApplied(targetKey, storage) {
  if (!targetKey) return false;
  const s = getStorage(storage);
  if (!s) return false;
  try {
    return s.getItem(APPLIED_UPDATE_STORAGE_KEY) === String(targetKey);
  } catch (_) {
    return false;
  }
}

export function markUpdateApplied(targetKey, storage) {
  if (!targetKey) return;
  const s = getStorage(storage);
  if (!s) return;
  try {
    s.setItem(APPLIED_UPDATE_STORAGE_KEY, String(targetKey));
  } catch (_) { /* sessionStorage unavailable — non-fatal */ }
}

// Auto-apply only on /login (never /sso-callback or /magic-link where a one-time
// token exchange is in flight) or right after an explicit F5 reload when idle.
export function shouldAutoApplyUpdate({
  pathname = '',
  navType = 'navigate',
  elapsedMs = Infinity,
  inCall = false,
  targetKey = '',
  storage,
} = {}) {
  if (inCall || !targetKey || wasUpdateApplied(targetKey, storage)) return false;
  if (pathname === '/login') return true;
  return isExplicitReloadNavigation(navType) && elapsedMs >= 0 && elapsedMs <= RELOAD_AUTO_APPLY_WINDOW_MS;
}

export function consumePreloadGuard(targetKey, storage) {
  if (!targetKey) return false;
  const s = getStorage(storage);
  if (!s) return false;
  try {
    const key = String(targetKey);
    if (s.getItem(PRELOAD_GUARD_STORAGE_KEY) === key) return false;
    s.setItem(PRELOAD_GUARD_STORAGE_KEY, key);
    return true;
  } catch (_) {
    return false;
  }
}

export function resolveFetchCacheOption(options = {}) {
  if (options && options.cache !== undefined) return options.cache;
  const method = String(options?.method ?? 'GET').toUpperCase();
  return method === 'GET' ? 'no-store' : undefined;
}
